<template>
  <div>
    <AdminNav />
    <main class="page">
      <header class="page-header">
        <div>
          <h2 class="page-title">Pilotage financier</h2>
          <p class="subtitle">Indicateurs de gestion indicatifs, à valider avec le comptable.</p>
        </div>
        <div class="period">
          <input v-model="periode.date_debut" class="input" type="date" @change="chargerPilotage" />
          <input v-model="periode.date_fin" class="input" type="date" @change="chargerPilotage" />
        </div>
      </header>

      <p class="notice">Indicateurs de gestion indicatifs · À valider avec le comptable · CAF estimée simplifiée</p>
      <p class="success-message" v-if="message">{{ message }}</p>
      <p class="error-message" v-if="erreur">{{ erreur }}</p>

      <nav class="tabs">
        <button v-for="tab in tabs" :key="tab.id" class="tab" :class="{ active: onglet === tab.id }" @click="onglet = tab.id">
          {{ tab.label }}
        </button>
      </nav>

      <section v-if="onglet === 'overview'" class="stack">
        <div class="kpis">
          <article v-for="kpi in kpis" :key="kpi.label" class="kpi">
            <span>{{ kpi.label }}</span>
            <strong>{{ kpi.money ? formatMoney(kpi.value) : kpi.value }}</strong>
          </article>
        </div>
        <section class="panel">
          <div class="panel-head">
            <h3>Alertes</h3>
            <span class="badge">{{ pilotage.alertes?.length || 0 }}</span>
          </div>
          <div class="empty" v-if="!pilotage.alertes || !pilotage.alertes.length">Aucune alerte sur la période.</div>
          <div v-for="alerte in pilotage.alertes" :key="alerte.type + alerte.message" class="alert-row">
            <strong>{{ alerte.message }}</strong>
            <span v-if="alerte.montant">{{ formatMoney(alerte.montant) }}</span>
          </div>
        </section>
      </section>

      <section v-if="onglet === 'abonnements'" class="layout">
        <section class="panel">
          <div class="panel-head">
            <h3>Abonnements</h3>
            <button class="btn btn-secondary" @click="nouvelAbonnement">Nouveau</button>
          </div>
          <div class="empty" v-if="!abonnements.length">Aucun abonnement.</div>
          <button v-for="a in abonnements" :key="a.id" class="item-row" :class="{ active: abonnementForm.id === a.id }" @click="editerAbonnement(a)">
            <span>
              <strong>{{ a.nom_enseigne || 'Commerçant' }}</strong>
              <small>{{ a.libelle }} · {{ libellePeriodicite(a.periodicite) }}</small>
            </span>
            <span>
              <em>{{ formatMoney(a.montant_ttc) }}</em>
              <small class="badge" :class="'badge-' + a.statut">{{ libelleAbonnement(a.statut) }}</small>
            </span>
          </button>
        </section>

        <section class="panel">
          <div class="panel-head">
            <h3>{{ abonnementForm.id ? 'Modifier abonnement' : 'Créer abonnement' }}</h3>
            <button class="btn btn-secondary" disabled title="À venir">Créer facture</button>
          </div>
          <div class="grid">
            <label class="field">Commerçant
              <select v-model="abonnementForm.commercant_id" class="input">
                <option value="">Sélectionner</option>
                <option v-for="c in commercants" :key="c.id" :value="c.id">{{ c.nom_enseigne }}</option>
              </select>
            </label>
            <label class="field">Libellé<input v-model="abonnementForm.libelle" class="input" /></label>
            <label class="field">Montant HT<input v-model.number="abonnementForm.montant_ht" class="input" type="number" min="0" step="0.01" /></label>
            <label class="field">Montant TTC<input v-model.number="abonnementForm.montant_ttc" class="input" type="number" min="0" step="0.01" /></label>
            <label class="field">Périodicité
              <select v-model="abonnementForm.periodicite" class="input">
                <option value="mensuel">Mensuel</option>
                <option value="annuel">Annuel</option>
                <option value="ponctuel">Ponctuel</option>
              </select>
            </label>
            <label class="field">Statut
              <select v-model="abonnementForm.statut" class="input">
                <option value="actif">Actif</option>
                <option value="suspendu">Suspendu</option>
                <option value="resilie">Résilié</option>
              </select>
            </label>
            <label class="field">Début<input v-model="abonnementForm.date_debut" class="input" type="date" /></label>
            <label class="field">Prochaine facturation<input v-model="abonnementForm.prochaine_facturation" class="input" type="date" /></label>
            <label class="field">Mode règlement<input v-model="abonnementForm.mode_reglement" class="input" /></label>
          </div>
          <label class="field">Notes<textarea v-model="abonnementForm.notes" class="input textarea"></textarea></label>
          <div class="actions">
            <button class="btn btn-primary" @click="enregistrerAbonnement" :disabled="loading">Enregistrer</button>
            <button class="btn btn-danger" @click="supprimerAbonnement" :disabled="!abonnementForm.id || loading">Supprimer</button>
          </div>
          <p class="hint">Créer facture depuis abonnement : à venir en V1.</p>
        </section>
      </section>

      <section v-if="onglet === 'charges'" class="layout">
        <section class="panel">
          <div class="panel-head">
            <h3>Charges</h3>
            <button class="btn btn-secondary" @click="nouvelleCharge">Nouvelle</button>
          </div>
          <div class="empty" v-if="!charges.length">Aucune charge.</div>
          <button v-for="charge in charges" :key="charge.id" class="item-row" :class="{ active: chargeForm.id === charge.id }" @click="editerCharge(charge)">
            <span>
              <strong>{{ charge.libelle }}</strong>
              <small>{{ charge.categorie }} · {{ formatDate(charge.date_charge) }}</small>
            </span>
            <span>
              <em>{{ formatMoney(charge.montant_ttc) }}</em>
              <small class="badge" :class="'badge-' + charge.statut">{{ libelleCharge(charge.statut) }}</small>
            </span>
          </button>
        </section>

        <section class="panel">
          <h3>{{ chargeForm.id ? 'Modifier charge' : 'Créer charge' }}</h3>
          <div class="grid">
            <label class="field">Libellé<input v-model="chargeForm.libelle" class="input" /></label>
            <label class="field">Catégorie
              <select v-model="chargeForm.categorie" class="input">
                <option v-for="categorie in categoriesCharges" :key="categorie">{{ categorie }}</option>
              </select>
            </label>
            <label class="field">Montant HT<input v-model.number="chargeForm.montant_ht" class="input" type="number" min="0" step="0.01" /></label>
            <label class="field">Montant TTC<input v-model.number="chargeForm.montant_ttc" class="input" type="number" min="0" step="0.01" /></label>
            <label class="field">Statut
              <select v-model="chargeForm.statut" class="input">
                <option value="a_payer">À payer</option>
                <option value="payee">Payée</option>
                <option value="annulee">Annulée</option>
              </select>
            </label>
            <label class="field">Date charge<input v-model="chargeForm.date_charge" class="input" type="date" /></label>
            <label class="field">Échéance<input v-model="chargeForm.date_echeance" class="input" type="date" /></label>
            <label class="field">Fournisseur<input v-model="chargeForm.fournisseur" class="input" /></label>
            <label class="field check"><input v-model="chargeForm.recurrente" type="checkbox" /> Récurrente</label>
            <label class="field">Périodicité
              <select v-model="chargeForm.periodicite" class="input">
                <option value="">Non définie</option>
                <option value="mensuel">Mensuel</option>
                <option value="annuel">Annuel</option>
                <option value="ponctuel">Ponctuel</option>
              </select>
            </label>
          </div>
          <label class="field">Notes<textarea v-model="chargeForm.notes" class="input textarea"></textarea></label>
          <div class="actions">
            <button class="btn btn-primary" @click="enregistrerCharge" :disabled="loading">Enregistrer</button>
            <button class="btn btn-secondary" @click="ajouterJustificatifCharge" :disabled="!chargeForm.id || loading">Ajouter justificatif</button>
            <button class="btn btn-danger" @click="supprimerCharge" :disabled="!chargeForm.id || loading">Supprimer</button>
          </div>
        </section>
      </section>

      <section v-if="onglet === 'paiements'" class="layout">
        <section class="panel wide">
          <div class="panel-head">
            <h3>Factures à encaisser</h3>
            <button class="btn btn-secondary" @click="chargerFactures">Actualiser</button>
          </div>
          <div class="empty" v-if="!facturesAEncaisser.length">Aucune facture à encaisser.</div>
          <div v-for="facture in facturesAEncaisser" :key="facture.id" class="payment-card">
            <div>
              <strong>{{ facture.numero }}</strong>
              <small>{{ facture.client_nom || facture.nom_enseigne }} · {{ libelleFacture(facture.statut) }}</small>
            </div>
            <div class="payment-amounts">
              <span>Total {{ formatMoney(facture.total_ttc) }}</span>
              <span>Reçu {{ formatMoney(facture.total_paye) }}</span>
              <strong>Reste {{ formatMoney(resteFacture(facture)) }}</strong>
            </div>
            <div class="payment-form">
              <input v-model.number="paiementForms[facture.id].montant" class="input" type="number" min="0" step="0.01" />
              <input v-model="paiementForms[facture.id].date_paiement" class="input" type="date" />
              <select v-model="paiementForms[facture.id].mode_reglement" class="input">
                <option>À définir</option>
                <option>Virement bancaire</option>
                <option>Espèces</option>
                <option>Carte bancaire</option>
                <option>Chèque</option>
                <option>Prélèvement</option>
                <option>Autre</option>
              </select>
              <button class="btn btn-primary" @click="enregistrerPaiement(facture)" :disabled="loading">Enregistrer paiement</button>
            </div>
          </div>
        </section>
      </section>
    </main>
  </div>
</template>

<script>
import AdminNav from '../../components/AdminNav.vue'
import adminApi from '../../adminApi'

export default {
  name: 'AdminGestionFinanciere',
  components: { AdminNav },
  data() {
    return {
      tabs: [
        { id: 'overview', label: 'Vue d’ensemble' },
        { id: 'abonnements', label: 'Abonnements' },
        { id: 'charges', label: 'Charges' },
        { id: 'paiements', label: 'Paiements' }
      ],
      categoriesCharges: ['Hébergement', 'Nom de domaine', 'Impression cartes', 'Matériel', 'Déplacement', 'Comptable', 'Banque', 'Logiciel', 'Communication', 'Divers'],
      onglet: 'overview',
      loading: false,
      erreur: '',
      message: '',
      pilotage: {},
      abonnements: [],
      charges: [],
      factures: [],
      commercants: [],
      paiementForms: {},
      periode: this.periodeDefaut(),
      abonnementForm: this.abonnementVide(),
      chargeForm: this.chargeVide()
    }
  },
  computed: {
    kpis() {
      return [
        { label: 'MRR', value: this.pilotage.mrr, money: true },
        { label: 'ARR', value: this.pilotage.arr, money: true },
        { label: 'CA encaissé', value: this.pilotage.ca_encaisse, money: true },
        { label: 'Créances clients', value: this.pilotage.creances_clients, money: true },
        { label: 'Charges à payer', value: this.pilotage.charges_a_payer, money: true },
        { label: 'BFR simplifié', value: this.pilotage.bfr_simplifie, money: true },
        { label: 'CAF estimée', value: this.pilotage.caf_estimee, money: true },
        { label: 'Abonnements actifs', value: this.pilotage.abonnements_actifs || 0 }
      ]
    },
    facturesAEncaisser() {
      return this.factures.filter(f => !['brouillon', 'payee', 'annulee'].includes(f.statut))
    }
  },
  methods: {
    today() { return new Date().toISOString().slice(0, 10) },
    periodeDefaut() {
      const now = new Date()
      const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString().slice(0, 10)
      const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0)).toISOString().slice(0, 10)
      return { date_debut: start, date_fin: end }
    },
    abonnementVide() {
      return {
        id: '',
        commercant_id: '',
        libelle: 'Abonnement Fidmarket',
        montant_ht: 0,
        montant_ttc: 0,
        tva_applicable: false,
        taux_tva: 0,
        periodicite: 'mensuel',
        statut: 'actif',
        date_debut: this.today(),
        date_fin: '',
        prochaine_facturation: '',
        mode_reglement: 'À définir',
        delai_paiement_jours: 0,
        notes: ''
      }
    },
    chargeVide() {
      return {
        id: '',
        libelle: '',
        categorie: 'Hébergement',
        montant_ht: 0,
        montant_ttc: 0,
        tva_applicable: false,
        taux_tva: 0,
        date_charge: this.today(),
        date_echeance: '',
        statut: 'a_payer',
        recurrente: false,
        periodicite: '',
        fournisseur: '',
        mode_reglement: 'À définir',
        notes: ''
      }
    },
    formatMoney(value) {
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(value || 0))
    },
    formatDate(value) { return value ? new Date(value).toLocaleDateString('fr-FR') : 'Non définie' },
    formatDateInput(value) { return value ? String(value).slice(0, 10) : '' },
    libellePeriodicite(value) { return { mensuel: 'Mensuel', annuel: 'Annuel', ponctuel: 'Ponctuel' }[value] || value },
    libelleAbonnement(value) { return { actif: 'Actif', suspendu: 'Suspendu', resilie: 'Résilié' }[value] || value },
    libelleCharge(value) { return { a_payer: 'À payer', payee: 'Payée', annulee: 'Annulée' }[value] || value },
    libelleFacture(value) {
      return { brouillon: 'Brouillon', envoyee: 'Envoyée', partiellement_payee: 'Partielle', payee: 'Payée', annulee: 'Annulée' }[value] || value
    },
    resteFacture(facture) {
      return Math.max(0, Number(facture.total_ttc || 0) - Number(facture.total_paye || 0))
    },
    async chargerInitial() {
      await Promise.all([
        this.chargerCommercants(),
        this.chargerPilotage(),
        this.chargerAbonnements(),
        this.chargerCharges(),
        this.chargerFactures()
      ])
    },
    async chargerCommercants() {
      const res = await adminApi.get('/commercants')
      this.commercants = res.data || []
    },
    async chargerPilotage() {
      const res = await adminApi.get('/pilotage-financier', { params: this.periode })
      this.pilotage = res.data || {}
    },
    async chargerAbonnements() {
      const res = await adminApi.get('/abonnements')
      this.abonnements = res.data || []
    },
    async chargerCharges() {
      const res = await adminApi.get('/charges-financieres')
      this.charges = res.data || []
    },
    async chargerFactures() {
      const res = await adminApi.get('/factures')
      this.factures = res.data || []
      this.factures.forEach(facture => {
        if (!this.paiementForms[facture.id]) {
          this.paiementForms[facture.id] = {
            montant: this.resteFacture(facture),
            date_paiement: this.today(),
            mode_reglement: facture.mode_reglement || 'À définir'
          }
        }
      })
    },
    nouvelAbonnement() { this.abonnementForm = this.abonnementVide() },
    editerAbonnement(a) {
      this.abonnementForm = {
        ...this.abonnementVide(),
        ...a,
        date_debut: this.formatDateInput(a.date_debut),
        date_fin: this.formatDateInput(a.date_fin),
        prochaine_facturation: this.formatDateInput(a.prochaine_facturation)
      }
    },
    async enregistrerAbonnement() {
      this.loading = true
      this.erreur = ''
      this.message = ''
      try {
        const payload = { ...this.abonnementForm }
        const res = payload.id
          ? await adminApi.put(`/abonnements/${payload.id}`, payload)
          : await adminApi.post('/abonnements', payload)
        await this.chargerAbonnements()
        await this.chargerPilotage()
        this.editerAbonnement(res.data)
        this.message = 'Abonnement enregistré.'
      } catch (err) {
        this.erreur = err.response?.data?.error || 'Impossible d’enregistrer l’abonnement.'
      } finally {
        this.loading = false
      }
    },
    async supprimerAbonnement() {
      if (!confirm('Supprimer cet abonnement ?')) return
      this.loading = true
      this.erreur = ''
      try {
        await adminApi.delete(`/abonnements/${this.abonnementForm.id}`)
        await this.chargerAbonnements()
        await this.chargerPilotage()
        this.nouvelAbonnement()
        this.message = 'Abonnement supprimé.'
      } catch (err) {
        this.erreur = err.response?.data?.error || 'Impossible de supprimer l’abonnement.'
      } finally {
        this.loading = false
      }
    },
    nouvelleCharge() { this.chargeForm = this.chargeVide() },
    editerCharge(charge) {
      this.chargeForm = {
        ...this.chargeVide(),
        ...charge,
        date_charge: this.formatDateInput(charge.date_charge),
        date_echeance: this.formatDateInput(charge.date_echeance)
      }
    },
    async enregistrerCharge() {
      this.loading = true
      this.erreur = ''
      this.message = ''
      try {
        const payload = { ...this.chargeForm }
        const res = payload.id
          ? await adminApi.put(`/charges-financieres/${payload.id}`, payload)
          : await adminApi.post('/charges-financieres', payload)
        await this.chargerCharges()
        await this.chargerPilotage()
        this.editerCharge(res.data)
        this.message = 'Charge enregistrée.'
      } catch (err) {
        this.erreur = err.response?.data?.error || 'Impossible d’enregistrer la charge.'
      } finally {
        this.loading = false
      }
    },
    async supprimerCharge() {
      if (!confirm('Supprimer cette charge ?')) return
      this.loading = true
      this.erreur = ''
      try {
        await adminApi.delete(`/charges-financieres/${this.chargeForm.id}`)
        await this.chargerCharges()
        await this.chargerPilotage()
        this.nouvelleCharge()
        this.message = 'Charge supprimée.'
      } catch (err) {
        this.erreur = err.response?.data?.error || 'Impossible de supprimer la charge.'
      } finally {
        this.loading = false
      }
    },
    ajouterJustificatifCharge() {
      this.$router.push({
        path: '/admin/justificatifs',
        query: {
          charge_id: this.chargeForm.id,
          type: 'charge',
          categorie: this.chargeForm.categorie || 'Autre',
          libelle: this.chargeForm.libelle
        }
      })
    },
    async enregistrerPaiement(facture) {
      this.loading = true
      this.erreur = ''
      this.message = ''
      try {
        await adminApi.post(`/factures/${facture.id}/paiements`, this.paiementForms[facture.id])
        await Promise.all([this.chargerFactures(), this.chargerPilotage()])
        this.message = 'Paiement enregistré.'
      } catch (err) {
        this.erreur = err.response?.data?.error || 'Impossible d’enregistrer le paiement.'
      } finally {
        this.loading = false
      }
    }
  },
  mounted() {
    this.chargerInitial().catch(err => {
      this.erreur = err.response?.data?.error || 'Impossible de charger la gestion financière.'
    })
  }
}
</script>

<style scoped>
.page {
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 14px;
  box-sizing: border-box;
}
.page-header {
  display: grid;
  gap: 12px;
  margin-bottom: 12px;
}
.page-title {
  margin: 0 0 4px;
  color: #1a202c;
  font-size: 22px;
  font-weight: 700;
}
.subtitle, .hint {
  margin: 0;
  color: #718096;
  font-size: 13px;
  line-height: 1.4;
}
.notice {
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f7fafc;
  color: #2d3748;
  font-size: 13px;
  font-weight: 700;
}
.period, .tabs, .actions {
  display: grid;
  gap: 8px;
}
.tabs {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-bottom: 14px;
}
.tab {
  min-height: 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  color: #4a5568;
  font-weight: 700;
}
.tab.active {
  background: #2d3748;
  color: white;
  border-color: #2d3748;
}
.stack, .layout, .kpis {
  display: grid;
  gap: 14px;
}
.kpis {
  grid-template-columns: 1fr;
}
.kpi, .panel {
  min-width: 0;
  border-radius: 8px;
  background: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}
.kpi {
  padding: 14px;
  display: grid;
  gap: 6px;
}
.kpi span, .field, .payment-amounts span, .item-row small, .payment-card small {
  color: #718096;
  font-size: 12px;
  font-weight: 700;
}
.kpi strong {
  color: #1a202c;
  font-size: 22px;
}
.panel {
  padding: 14px;
}
.panel h3 {
  margin: 0;
  color: #2d3748;
  font-size: 16px;
}
.panel-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}
.field {
  display: grid;
  gap: 5px;
  min-width: 0;
}
.check {
  grid-template-columns: auto 1fr;
  align-items: center;
  min-height: 40px;
}
.input {
  width: 100%;
  min-height: 40px;
  padding: 9px 11px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: #fff;
  box-sizing: border-box;
}
.textarea {
  min-height: 76px;
  resize: vertical;
}
.item-row, .alert-row, .payment-card {
  width: 100%;
  border: 1px solid #edf2f7;
  border-radius: 8px;
  background: #fff;
  padding: 12px;
  box-sizing: border-box;
}
.item-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  margin-bottom: 8px;
  text-align: left;
  cursor: pointer;
}
.item-row.active, .item-row:hover {
  border-color: #2d3748;
  background: #f7fafc;
}
.item-row span {
  display: grid;
  gap: 4px;
  min-width: 0;
}
.item-row em {
  color: #1a202c;
  font-style: normal;
  font-weight: 700;
}
.payment-card {
  display: grid;
  gap: 12px;
  margin-bottom: 10px;
}
.payment-amounts, .payment-form {
  display: grid;
  gap: 8px;
}
.alert-row {
  display: grid;
  gap: 4px;
  margin-bottom: 8px;
}
.btn {
  min-height: 40px;
  padding: 10px 14px;
  border: 0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}
.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.btn-primary { background: #2d3748; color: white; }
.btn-secondary { background: #edf2f7; color: #4a5568; }
.btn-danger { background: #fed7d7; color: #742a2a; }
.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  border-radius: 999px;
  padding: 4px 10px;
  background: #edf2f7;
  color: #4a5568;
  font-size: 12px;
  font-weight: 700;
}
.badge-actif, .badge-payee { background: #c6f6d5; color: #22543d; }
.badge-suspendu, .badge-a_payer, .badge-partiellement_payee { background: #fff3cd; color: #744210; }
.badge-resilie, .badge-annulee { background: #fed7d7; color: #742a2a; }
.empty {
  color: #718096;
  font-size: 14px;
  padding: 10px 0;
}
.success-message, .error-message {
  padding: 10px 12px;
  border-radius: 8px;
  margin-bottom: 12px;
}
.success-message { color: #22543d; background: #c6f6d5; }
.error-message { color: #742a2a; background: #fed7d7; }

@media (min-width: 700px) {
  .page { padding: 22px; }
  .page-header {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: start;
  }
  .period { grid-template-columns: 150px 150px; }
  .tabs { grid-template-columns: repeat(4, minmax(0, 1fr)); }
  .kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .actions { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .item-row { grid-template-columns: minmax(0, 1fr) auto; }
  .payment-form { grid-template-columns: 120px 150px minmax(150px, 1fr) auto; }
}

@media (min-width: 1020px) {
  .layout { grid-template-columns: minmax(300px, 380px) minmax(0, 1fr); }
  .kpis { grid-template-columns: repeat(4, minmax(0, 1fr)); }
  .wide { grid-column: 1 / -1; }
}
</style>
